import React, { useEffect, useState } from 'react';
import { SaveOutlined } from '@ant-design/icons';
import TimePickerField from '@components/common/form/TimePickerField';
import { TIME_FORMAT_DISPLAY } from '@constants';
import { daysOfWeekSchedule as daysOfWeekScheduleOptions } from '@constants/masterData';
import useTranslate from '@hooks/useTranslate';
import { CheckboxField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { getDisabledHours, getDisabledMinutes } from '@utils/dayHelper';
import { Button, Checkbox, Col, Flex, Form, Row } from 'antd';

import './index.scss';

const isMidnight = (value) => !!value && value.hour() === 0 && value.minute() === 0;

const getDisabledMinutesForTo = (hour, fromValue) => {
    const baseDisabled = getDisabledMinutes(hour, fromValue) || [];

    if (
        fromValue &&
        fromValue.hour() === hour &&
        !isMidnight(fromValue)
    ) {
        return [ ...new Set([ ...baseDisabled, fromValue.minute() ]) ];
    }

    return baseDisabled;
};

const HappyHourSettingForm = ({
    formId,
    settingObj,
    onSubmit,
    setIsChangedFormValues,
    isChangedFormValues,
    loadingUpdate,
}) => {
    const translate = useTranslate();
    const [ form ] = Form.useForm();
    const daysOfWeekSchedule = translate.formatKeys(daysOfWeekScheduleOptions, [ 'label' ]);
    useEffect(() => {
        if (settingObj) {
            form.setFieldsValue(settingObj);
        }
    }, [ settingObj, form ]);

    const onValuesChange = () => {
        setIsChangedFormValues(true);
    };
    const [ forceRenderKeys, setForceRenderKeys ] = useState({});

    const onSelectHappyHourTime = (fieldPath, value) => {
        const [ index, timeType ] = fieldPath;
        const happyHours = form.getFieldValue("happy_hours_settings");

        if (timeType === "from") {
            // if (happyHours[index]["to"] && value && happyHours[index]["to"].isBefore(value)) {
            //     happyHours[index]["to"] = null;
            // }
            const currentTo = happyHours[index]["to"];
            const toIsInvalid =
                currentTo &&
                value &&
                (
                    currentTo.isBefore(value) ||
                    (currentTo.isSame(value) && !isMidnight(value))
                );

            if (toIsInvalid) {
                happyHours[index]["to"] = null;
            }
            setForceRenderKeys((prev) => ({
                ...prev,
                [`${index}_to`]: (prev[`${index}_to`] || 0) + 1,
                [`${index}_from`]: (prev[`${index}_from`] || 0) + 1,
            }));
        } else if (timeType === "to") {
            // if (happyHours[index]["from"] && value && value.isBefore(happyHours[index]["from"])) {
            //     value = happyHours[index]["from"];
            // }
            const fromValue = happyHours[index]["from"];
            if (fromValue && value) {
                if (value.isBefore(fromValue)) {
                    value = fromValue;
                } else if (value.isSame(fromValue) && !isMidnight(fromValue)) {
                    return;
                }
            }
        }

        happyHours[index][timeType] = value;
        form.setFieldsValue({ happy_hours_settings: happyHours });
        onValuesChange();
    };

    return (
        <Form
            form={form}
            layout="horizontal"
            onFinish={onSubmit}
            initialValues={settingObj}
            onValuesChange={onValuesChange}
            id={formId}
        >
            <table className="happy-hours-tablee">
                <thead>
                    <tr>
                        <th width="14%">{translate.formatMessage(commonMessage.timeFrame)}</th>
                        <th>{translate.formatMessage(commonMessage.timeOfWeek)}</th>
                        <th width="14%">{translate.formatMessage(commonMessage.from)}</th>
                        <th width="14%">{translate.formatMessage(commonMessage.to)}</th>
                    </tr>
                </thead>
                <tbody>
                    <Form.List name="happy_hours_settings">
                        {(fields) => {
                            return fields.map((field, index) => (
                                <tr key={field.key}>
                                    <td>
                                        {translate.formatMessage(commonMessage.timeFrame)} {index + 1}
                                    </td>
                                    <td className="td-name">
                                        <Form.Item name={[ field.name, 'time_of_weeks' ]} style={{ width: '100%' }}>
                                            <Checkbox.Group style={{ width: '100%' }}>
                                                <Row>
                                                    {daysOfWeekSchedule.map((option) => (
                                                        <Col span={3} key={option.label}>
                                                            <Checkbox value={option.value}>{option.label}</Checkbox>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Checkbox.Group>
                                        </Form.Item>
                                    </td>
                                    <td>
                                        <TimePickerField
                                            key={forceRenderKeys[`${index}_from`]}
                                            size="small"
                                            fieldName={[ field.name, 'from' ]}
                                            onChange={(value) => onSelectHappyHourTime([ field.name, 'from' ], value)}
                                            width="100%"
                                            required
                                            requiredMsg={translate.formatMessage(commonMessage.requireTime)}
                                            validateTrigger={[ 'onChange', 'onBlur' ]}
                                            onClick={onValuesChange}
                                        />
                                    </td>
                                    <td>
                                        <TimePickerField
                                            size="small"
                                            fieldName={[ field.name, 'to' ]}
                                            onChange={(value) => onSelectHappyHourTime([ field.name, 'to' ], value)}
                                            width="100%"
                                            required
                                            requiredMsg={translate.formatMessage(commonMessage.requireTime)}
                                            disabledHours={() =>
                                                getDisabledHours(
                                                    form.getFieldValue([ 'happy_hours_settings', field.name, 'from' ]),
                                                )
                                            }
                                            disabledMinutes={(hour) =>
                                                getDisabledMinutesForTo(
                                                    hour,
                                                    form.getFieldValue([ 'happy_hours_settings', field.name, 'from' ]),
                                                )
                                            }
                                            validateTrigger={[ 'onChange', 'onBlur' ]}
                                            onClick={onValuesChange}
                                        />
                                    </td>
                                </tr>
                            ));
                        }}
                    </Form.List>
                </tbody>
            </table>
            <Flex justify="end">
                <Button
                    style={{ marginLeft: '10rem', marginTop: 12 }}
                    key="submit"
                    htmlType="submit"
                    type="primary"
                    disabled={!isChangedFormValues}
                    loading={loadingUpdate}
                >
                    <div>
                        <SaveOutlined style={{ marginRight: '6px' }} />
                        {translate.formatMessage(commonMessage.update)}
                    </div>
                </Button>
            </Flex>
        </Form>
    );
};

export default HappyHourSettingForm;
